import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirebaseApp } from '../config/firebase';

class AgentAvatarService {
    static instance = null;

    constructor() {
        if (AgentAvatarService.instance) {
            return AgentAvatarService.instance;
        }

        // Get Firebase app instance and initialize storage
        try {
            const app = getFirebaseApp();
            this.storage = getStorage(app);
            this.bucketName = 'ai-learnings.firebasestorage.app';
            this.urlCache = new Map(); // Cache for avatar URLs
            console.log('Firebase Storage initialized successfully');
        } catch (error) {
            console.error('Error initializing Firebase Storage:', error);
            throw error;
        }

        this.agentAvatarMap = {
            // Core Agents
            'website_analyzer_agent': 'website-analyzer.png',
            'outreach_assistant_agent': 'sales-assistant.png',
            'email_reviewer_agent': 'email-reviewer.png',
            'audit_outgoing_email_agent': 'audit-outgoing.png',
            'email_sender_agent': 'email-sender.png',
            'email_checker_agent': 'email-checker.png',
            // Note: digital_twin_agent is handled specially - uses prospect's actual image

            // Analysis & Response Agents
            'engagement_analysis_agent': 'engagement-analyzer.png',
            'email_response_generator_agent': 'email-response-generator.png',
            'email_responder_agent': 'email-responder.png',
            'follow_up_strategizer_agent': 'follow-up-strategizer.png',

            // Management & Coordination
            'chief_of_staff': 'chief-of-staff.png',
            'agent_mediator': 'agent-mediator.png',
            'human_proxy': 'human-proxy.png',

            // Legacy mappings for backward compatibility
            'WA': 'website-analyzer.png',
            'SA': 'sales-assistant.png',
            'ER': 'email-reviewer.png',
            'AO': 'audit-outgoing.png',
            'ES': 'email-sender.png',
            'EC': 'email-checker.png',
            'EAN': 'engagement-analyzer.png',
            'ERG': 'email-response-generator.png',
            'ERP': 'email-responder.png',
            'CO': 'chief-of-staff.png',
            'AM': 'agent-mediator.png',
            'HP': 'human-proxy.png'
        };

        AgentAvatarService.instance = this;
    }

    normalizeAgentName(name) {
        if (!name) return '';
        // Convert camelCase or PascalCase to underscore format first
        const underscored = name
            .replace(/([a-z])([A-Z])/g, '$1_$2')
            .toLowerCase();
        // Then normalize to standard format
        const normalized = underscored
            .split('_')
            .map(part => part.charAt(0).toUpperCase() + part.slice(1))
            .join('');
        console.log('Name normalization:', { original: name, normalized });
        return normalized;
    }

    async getAvatarUrl(agentId, agentName) {
        try {
            console.log('Getting avatar for:', { agentId, agentName });

            // Special handling for DigitalTwinAgent - check BEFORE normalization
            if (agentName && agentName.toLowerCase().includes('digitaltwin')) {
                console.log('[DigitalTwin] Checking for avatar:', {
                    hasProspectImage: !!window.currentProspectImage,
                    prospectImage: window.currentProspectImage,
                    hasProspectName: !!window.currentProspectName,
                    prospectName: window.currentProspectName
                });

                // If we have the prospect's actual image, use it
                if (window.currentProspectImage && window.currentProspectImage.trim() !== '') {
                    console.log('Using prospect actual image:', window.currentProspectImage);
                    return window.currentProspectImage;
                }

                // Always generate avatar with proper initials - never return null
                const displayName = window.currentProspectName || 'Digital Twin';
                const nameParts = displayName.trim().split(/\s+/).filter(p => p.length > 0);
                let initials;

                if (displayName && displayName !== 'Digital Twin' && nameParts.length >= 2) {
                    // Take first letter of first and last name
                    initials = (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
                } else if (displayName && displayName !== 'Digital Twin' && nameParts.length === 1) {
                    // Take first two letters of single name
                    initials = nameParts[0].slice(0, 2).toUpperCase();
                } else {
                    // Default to DT but this should rarely happen
                    initials = 'DT';
                    console.warn('[DigitalTwin] No prospect name available, using default DT initials');
                }

                // Use a more reliable avatar service with fallback
                const prospectAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&background=8B6914&color=ffffff&size=128&bold=true&format=png`;
                console.log('[DigitalTwin] Generated avatar URL:', prospectAvatar, 'Name:', displayName, 'Initials:', initials);
                return prospectAvatar;
            }

            // Skip cache for DigitalTwinAgent since it changes with each prospect
            const isDigitalTwin = agentName && agentName.toLowerCase().includes('digitaltwin');

            agentName = this.normalizeAgentName(agentName);

            // Check cache first (but not for DigitalTwinAgent)
            const cacheKey = isDigitalTwin
                ? `${agentId}_${agentName}_${window.currentProspectName}_${window.currentProspectImage}`
                : `${agentId}_${agentName}`;

            if (!isDigitalTwin && this.urlCache.has(cacheKey)) {
                console.log('Returning cached avatar URL for:', cacheKey);
                return this.urlCache.get(cacheKey);
            }

            // Special case for system messages - use local default avatar
            if (agentId === 'system' || agentName === 'system') {
                const defaultUrl = '/agent-avatars/default-avatar.svg';
                console.log('Using default avatar for system:', defaultUrl);
                this.urlCache.set(cacheKey, defaultUrl);
                return defaultUrl;
            }

            // First, check if we have a mapped filename for this agent
            const avatarFilename = this.agentAvatarMap[agentId];
            if (avatarFilename) {
                const url = `https://firebasestorage.googleapis.com/v0/b/${this.bucketName}/o/agent-avatars%2F${avatarFilename}?alt=media`;
                console.log('Using mapped avatar URL:', url);
                this.urlCache.set(cacheKey, url);
                return url;
            }

            // If no mapping exists, try using the agent name
            if (agentName) {
                // First convert camelCase to hyphenated (e.g. EmailReviewerAgent -> email-reviewer)
                let normalizedName = agentName
                    .replace(/([a-z])([A-Z])/g, '$1-$2')
                    .toLowerCase()
                    .replace(/\bagent\b/gi, '') // Remove 'agent' word (case insensitive)
                    .replace(/[\s_]+/g, '-') // Replace spaces and underscores with hyphens
                    .replace(/-+/g, '-') // Replace multiple consecutive hyphens with a single one
                    .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens

                const storagePath = `agent-avatars/${normalizedName}.png`;
                const url = `https://firebasestorage.googleapis.com/v0/b/${this.bucketName}/o/${encodeURIComponent(storagePath)}?alt=media`;
                console.log('Using normalized name URL:', url);
                this.urlCache.set(cacheKey, url);
                return url;
            }

            // If no specific avatar is found, return the default avatar
            const defaultUrl = '/agent-avatars/default-avatar.svg';
            console.log('Using default avatar URL:', defaultUrl);
            this.urlCache.set(cacheKey, defaultUrl);
            return defaultUrl;
        } catch (error) {
            console.error('Error getting avatar URL:', error);
            // Return default avatar on error
            return '/agent-avatars/default-avatar.svg';
        }
    }

    async getAgentAvatar(agentId) {
        // Use the unified getAvatarUrl method to ensure consistent URL generation
        return this.getAvatarUrl(agentId);
    }

    // Upload a new avatar for an agent
    async uploadAgentAvatar(agentId, file) {
        try {
            const normalizedId = agentId.toLowerCase().replace(/_/g, '-');
            const filename = `${normalizedId}.png`;
            const agentAvatarRef = ref(this.storage, `agent-avatars/${filename}`);
            await uploadBytes(agentAvatarRef, file);
            const url = await getDownloadURL(agentAvatarRef);

            // Update cache
            this.urlCache.clear(); // Clear entire cache since we have a new upload
            return url;
        } catch (error) {
            console.error('Error uploading avatar:', error);
            throw error;
        }
    }
}

// Create and export singleton instance
const instance = new AgentAvatarService();
export default instance;